import { CalendarBlank, Sparkle } from "@phosphor-icons/react"

import { Badge } from "~/components/design-system/components"
import type { BadgeProps } from "~/components/design-system/components"
import {
  adjustmentBandLabel,
  signalTypeForTheme,
  type SignalCategory,
  type SignalType,
} from "~/components/demand-2/shared/signalCatalog"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { formatName } from "~/utils/namingUtils"
import { type AdjustmentBand, windowCoverageEnd } from "~/utils/demandProjection"

type BadgeVariant = NonNullable<BadgeProps["variant"]>

// Manual-stage deltas are per-slice edits inside the Manual number, never campaigns; overlays are
// campaigns layered after it.
export type AdjustmentStage = "setup" | "manual" | "overlay" | undefined

export const stageLabel = (stage: AdjustmentStage) =>
  stage === "setup" ? "Demand signals" : stage === "manual" ? "Manual" : "Campaign"
// Low-chroma so the SignalBadge stays the only loud pill; violet would collide with Strategy.
export const stageVariant = (stage: AdjustmentStage): BadgeVariant =>
  stage === "setup" ? "neutral" : stage === "manual" ? "blue" : "yellow"

export const STAGE_COPY = {
  setup: {
    title: "Demand signals",
    where: "Folds into the base you start from.",
    empty: "No demand signals yet. Add one to shape the base.",
  },
  overlay: {
    title: "Campaigns",
    where: "Layers onto the total and never moves the base.",
    empty: "No campaigns yet. Add one to layer a temporary effect.",
  },
} as const

// The drivers of the Demand signals number, in build order. `preorder` and `reorder` are Model
// insights; `signal` is the planner's Strategy.
export const SETUP_LAYER_ORDER = ["preorder", "reorder", "signal"] as const
export type SetupLayer = (typeof SETUP_LAYER_ORDER)[number]

export const INSIGHT_GROUP_TITLE = "Model insights"
/** The planner's half of Demand signals: Strategic assumptions plus Strategic adjustments. */
export const STRATEGY_GROUP_TITLE = "Strategy"
/** The answers set in Demand configuration: Cannibalization, growth, and the pre-order floor. */
export const STRATEGIC_ASSUMPTIONS_TITLE = "Strategic assumptions"
/** The planner's own strategic rows, added by hand: New Channel, New Market, Custom. */
export const STRATEGIC_ADJUSTMENTS_TITLE = "Strategic adjustments"

// A function: `formatName` reads tenant naming at render.
export const setupLayerCopy = (layer: SetupLayer): { title: string; where: string } =>
  layer === "preorder"
    ? {
        title: formatName("preorder", false, true),
        where: `Structural change driven by ${formatName("preorder", true)}.`,
      }
    : layer === "reorder"
      ? {
          title: "Reorder",
          where: "Structural change driven by reorders.",
        }
      : {
          title: STRATEGY_GROUP_TITLE,
          where: "Planner knowledge and range-planning events.",
        }

export const InsightBadge = () => (
  <Badge
    variant="green"
    size="xs"
    title="Insight: sized from the model, not an authored adjustment"
    className="shrink-0 cursor-help"
  >
    <Sparkle className="size-3" weight="fill" />
  </Badge>
)

const bandOf = (target: string | null | undefined): AdjustmentBand =>
  target === "preorder" || target === "both" ? target : "sales"

// Named as the "Applies to" picker names it. A manual row folds into reorders only: no band to show.
export const TargetBadge = ({ target, stage }: { target?: string | null; stage?: string }) =>
  stage === "manual" ? null : (
    <Badge variant="mint" size="xs">
      {adjustmentBandLabel(bandOf(target))}
    </Badge>
  )

const categoryVariant = (category: SignalCategory): BadgeVariant =>
  category === "Strategy" ? "violet" : "gray"

const signalTitle = (signal: SignalType) =>
  signal.whyTemplate ? `${signal.description}: ${signal.whyTemplate}` : signal.description

export const SignalBadge = ({ theme }: { theme?: string | null }) => {
  const signal = signalTypeForTheme(theme)
  const Icon = signal.icon
  return (
    <Badge
      variant={categoryVariant(signal.category)}
      title={signalTitle(signal)}
      className="cursor-help"
    >
      <Icon className="size-3.5" />
      {signal.label}
    </Badge>
  )
}

// For rows whose own name already carries the text.
export const SignalIconBadge = ({ theme }: { theme?: string | null }) => {
  const signal = signalTypeForTheme(theme)
  const Icon = signal.icon
  return (
    <Badge
      variant={categoryVariant(signal.category)}
      title={signalTitle(signal)}
      className="shrink-0 cursor-help px-1"
    >
      <Icon className="size-3.5" />
    </Badge>
  )
}

// Renders nothing for the windowless whole-horizon default.
export const WindowBadge = ({
  startDate,
  endDate,
}: {
  startDate?: Date | string | null
  endDate?: Date | string | null
}) =>
  startDate && endDate ? (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap label-sm text-fg-muted"
      title="Active period (whole weeks)"
    >
      <CalendarBlank className="size-3" weight="bold" />
      {formatDate(startDate, DateFormatType.DayMonth)} –{" "}
      {/* The stored endDate is a week START; show the week's last covered day. */}
      {formatDate(windowCoverageEnd(new Date(endDate)), DateFormatType.DayMonth)}
    </span>
  ) : null
